import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Database, Printer, Copy, Trash2, Gem, Loader2 } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { CardSide } from "@/components/card/CardSide";
import { api, errMsg } from "@/lib/api";
import { CARD_SIZES, CATEGORY_LABEL } from "@/lib/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fitScale = (tpl, box = 230, boxH = 150) => Math.min(box / tpl.width, boxH / tpl.height);

const PresetCard = ({ p, onUse, busy, i }) => (
  <button onClick={() => onUse(p.id)} disabled={busy} data-testid={`preset-${p.id}`}
    className="group text-left rounded-xl border border-slate-800 bg-slate-900/60 hover:border-amber-500/60 hover:bg-slate-900 transition-colors p-4 rise"
    style={{ animationDelay: `${i * 60}ms` }}>
    <div className="h-[170px] grid place-items-center rounded-lg bg-slate-950 studio-grid overflow-hidden">
      <div className="shadow-xl shadow-black/50 group-hover:-translate-y-1 group-hover:rotate-[-1.5deg] transition-transform duration-300">
        <CardSide tpl={p} side="front" scale={fitScale(p)} rounded />
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between">
      <span className="font-semibold text-sm">{p.name}</span>
      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${p.category === "jewellery" ? "border-amber-500/50 text-amber-300" : "border-slate-700 text-slate-400"}`}>
        {p.category === "jewellery" && <Gem size={10} className="inline mr-1 -mt-0.5" />}{CATEGORY_LABEL[p.category]}
      </span>
    </div>
    <span className="text-[11px] font-mono text-slate-500">{p.width} × {p.height} mm</span>
  </button>
);

const BlankCreator = ({ onCreate, busy }) => {
  const [size, setSize] = useState("cr80");
  const [w, setW] = useState(85.6);
  const [h, setH] = useState(54);
  const pick = (id) => {
    setSize(id);
    const s = CARD_SIZES.find((x) => x.id === id);
    if (s) { setW(s.w); setH(s.h); }
  };
  return (
    <div className="rounded-xl border border-dashed border-slate-700 p-4 flex flex-col gap-3 rise" data-testid="blank-creator">
      <div className="h-[110px] grid place-items-center rounded-lg bg-slate-950 studio-grid">
        <div className="bg-white/90 rounded-md shadow-lg" style={{ width: (w / Math.max(w, h)) * 120, height: (h / Math.max(w, h)) * 120 }} />
      </div>
      <span className="font-semibold text-sm">Blank canvas</span>
      <select className="cf-input h-9 text-xs" value={size} onChange={(e) => pick(e.target.value)} data-testid="card-type-select">
        {CARD_SIZES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        <option value="custom">Custom size…</option>
      </select>
      {size === "custom" && (
        <div className="flex gap-2">
          <input type="number" step="0.1" className="cf-input h-9 text-xs" value={w} onChange={(e) => setW(+e.target.value)} data-testid="blank-width-input" />
          <input type="number" step="0.1" className="cf-input h-9 text-xs" value={h} onChange={(e) => setH(+e.target.value)} data-testid="blank-height-input" />
        </div>
      )}
      <button className="cf-btn-primary h-9 text-xs" disabled={busy || !w || !h} onClick={() => onCreate({ width: w, height: h, name: "Untitled design" })} data-testid="create-blank-button">
        <Plus size={14} /> Create blank
      </button>
    </div>
  );
};

const DesignCard = ({ t, nav, onDuplicate, onDelete }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 flex flex-col gap-3" data-testid={`design-card-${t.id}`}>
    <button onClick={() => nav(`/editor/${t.id}`)} className="h-[160px] grid place-items-center rounded-lg bg-slate-950 studio-grid overflow-hidden">
      <div className="shadow-xl shadow-black/50"><CardSide tpl={t} side="front" scale={fitScale(t, 220, 140)} rounded editing /></div>
    </button>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-semibold text-sm truncate" data-testid="design-card-name">{t.name}</p>
        <p className="text-[11px] font-mono text-slate-500">{t.width}×{t.height}mm · {t.record_count} records</p>
      </div>
      <div className="flex gap-1">
        <IconBtn label="Duplicate" onClick={() => onDuplicate(t.id)} testId={`design-duplicate-${t.id}`}><Copy size={14} /></IconBtn>
        <IconBtn label="Delete" onClick={() => onDelete(t)} testId={`design-delete-${t.id}`} danger><Trash2 size={14} /></IconBtn>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      <ActionBtn onClick={() => nav(`/editor/${t.id}`)} testId={`design-edit-${t.id}`}><Pencil size={13} /> Design</ActionBtn>
      <ActionBtn onClick={() => nav(`/data/${t.id}`)} testId={`design-data-${t.id}`}><Database size={13} /> Data</ActionBtn>
      <ActionBtn onClick={() => nav(`/print/${t.id}`)} testId={`design-print-${t.id}`}><Printer size={13} /> Print</ActionBtn>
    </div>
  </div>
);

const IconBtn = ({ children, onClick, label, testId, danger }) => (
  <button title={label} onClick={onClick} data-testid={testId}
    className={`p-1.5 rounded-md text-slate-400 hover:bg-slate-800 transition-colors ${danger ? "hover:text-red-400" : "hover:text-white"}`}>{children}</button>
);
const ActionBtn = ({ children, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="flex items-center justify-center gap-1.5 h-8 rounded-md border border-slate-700 text-xs font-medium hover:border-amber-500/60 hover:text-amber-200 transition-colors">{children}</button>
);

export default function Dashboard() {
  const nav = useNavigate();
  const [presets, setPresets] = useState([]);
  const [designs, setDesigns] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/templates").then((r) => setDesigns(r.data));
  useEffect(() => {
    api.get("/presets").then((r) => setPresets(r.data));
    load();
  }, []);

  const create = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post("/templates", body);
      toast.success("Design created");
      nav(`/editor/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const duplicate = async (id) => { await api.post(`/templates/${id}/duplicate`); toast.success("Duplicated"); load(); };
  const remove = async () => { await api.delete(`/templates/${toDelete.id}`); setToDelete(null); toast.success("Deleted"); load(); };

  return (
    <div className="min-h-screen bg-slate-950">
      <AppHeader />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 space-y-14">
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-[11px] font-mono text-amber-400 uppercase tracking-[0.2em]">Start a new card</p>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-1">Template gallery</h1>
            </div>
            {busy && <Loader2 className="animate-spin text-amber-400" />}
          </div>
          <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {presets.map((p, i) => <PresetCard key={p.id} p={p} i={i} busy={busy} onUse={(id) => create({ preset_id: id })} />)}
            <BlankCreator onCreate={create} busy={busy} />
          </div>
        </section>

        <section>
          <p className="text-[11px] font-mono text-slate-500 uppercase tracking-[0.2em]">Saved to your account</p>
          <h2 className="font-heading text-xl font-bold tracking-tight mt-1 mb-6">My designs</h2>
          {designs === null ? (
            <Loader2 className="animate-spin text-slate-500" />
          ) : designs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-800 p-10 text-center text-sm text-slate-400" data-testid="designs-empty">
              No designs yet — pick a template above to start.
            </div>
          ) : (
            <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="designs-grid">
              {designs.map((t) => <DesignCard key={t.id} t={t} nav={nav} onDuplicate={duplicate} onDelete={setToDelete} />)}
            </div>
          )}
        </section>
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{toDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This removes the design and all of its card records.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-button" className="bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
